
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Brain, BrainCircuit, Clock, Gauge, LineChart, AlertTriangle, ChevronRight, Zap } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface CognitiveFatigueReportProps {
  cognitiveData: {
    questionTimings: Record<string, number>;
    navigationPatterns: Array<{time: number, from: number, to: number}>;
    averageTimePerQuestion: number | null;
    fatigueZones: number[];
  };
}

const CognitiveFatigueReport: React.FC<CognitiveFatigueReportProps> = ({ cognitiveData }) => {
  if (!cognitiveData || !cognitiveData.questionTimings || Object.keys(cognitiveData.questionTimings).length === 0) {
    return (
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-gray-500 italic">
            <AlertTriangle className="h-4 w-4" />
            <p>Cognitive fatigue data not available for this test attempt.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Extract data for analysis
  const { questionTimings, navigationPatterns, fatigueZones } = cognitiveData;
  const timings = Object.entries(questionTimings).map(([id, time]) => ({ 
    questionId: parseInt(id), 
    time 
  })).sort((a, b) => a.questionId - b.questionId);

  // Calculate quick statistics
  const averageTime = Object.values(questionTimings).reduce((sum, time) => sum + time, 0) / Object.values(questionTimings).length;
  const fastestQuestion = Math.min(...Object.values(questionTimings));
  const slowestQuestion = Math.max(...Object.values(questionTimings));
  
  // Calculate erratic navigation
  const jumpCount = navigationPatterns ? navigationPatterns.filter((nav, idx) => {
    if (idx === 0) return false;
    const prevNav = navigationPatterns[idx - 1];
    return Math.abs(nav.to - prevNav.to) > 3; // Jump of more than 3 questions
  }).length : 0;
  
  // Identify burnout phases
  const burnoutZones = [];
  let currentZone = [];
  
  for (let i = 0; i < fatigueZones.length; i++) {
    if (currentZone.length === 0 || fatigueZones[i] - fatigueZones[i-1] <= 3) {
      currentZone.push(fatigueZones[i]);
    } else {
      if (currentZone.length >= 2) {
        burnoutZones.push([...currentZone]);
      }
      currentZone = [fatigueZones[i]];
    }
  }
  
  if (currentZone.length >= 2) {
    burnoutZones.push(currentZone);
  }
  
  // Calculate cognitive efficiency score (0-100)
  const calculateEfficiencyScore = () => {
    // Lower score is better
    let score = 100;
    
    // Penalty for high variance in question timing
    const variance = Object.values(questionTimings).reduce((sum, time) => {
      return sum + Math.pow(time - averageTime, 2);
    }, 0) / Object.values(questionTimings).length;
    const stdDev = Math.sqrt(variance);
    
    score -= Math.min(stdDev / averageTime * 30, 25);
    
    // Penalty for erratic navigation
    if (navigationPatterns) {
      score -= Math.min(jumpCount * 3, 20);
    }
    
    // Penalty for burnout zones
    score -= Math.min(burnoutZones.length * 10, 30);
    
    // Higher score is better (invert)
    return Math.max(0, Math.min(100, score));
  };
  
  const efficiencyScore = calculateEfficiencyScore();
  
  // Generate key insights
  const generateInsights = () => {
    const insights = [];
    
    if (burnoutZones.length > 0) {
      const formatZone = (zone) => {
        return `Q${zone[0]}-Q${zone[zone.length - 1]}`;
      };
      
      insights.push(`Fatigue detected in ${burnoutZones.length} section${burnoutZones.length > 1 ? 's' : ''}: ${burnoutZones.map(formatZone).join(', ')}`);
    }
    
    if (jumpCount > 5) {
      insights.push(`High erratic navigation detected (${jumpCount} major jumps between questions)`);
    }
    
    if (Object.values(questionTimings).filter(time => time < averageTime * 0.3).length > 5) {
      insights.push("Several questions answered too quickly, suggesting guessing behavior");
    }
    
    if (insights.length === 0) {
      insights.push("Consistent test-taking behavior with steady focus");
    }
    
    return insights;
  };
  
  const insights = generateInsights();
  
  return (
    <Card className="mb-6 overflow-hidden border-indigo-100">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-1/3">
            <div className="relative h-36 w-36 mx-auto">
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                <span className={cn(
                  "text-3xl font-bold", 
                  efficiencyScore >= 75 ? "text-green-600" : 
                  efficiencyScore >= 50 ? "text-blue-600" : 
                  efficiencyScore >= 30 ? "text-amber-600" : 
                  "text-red-600"
                )}>
                  {efficiencyScore}%
                </span>
                <span className="text-xs text-gray-500">Cognitive Efficiency</span>
              </div>
              <svg className="h-36 w-36 -rotate-90" viewBox="0 0 36 36">
                <path
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#eee"
                  strokeWidth="3"
                />
                <path
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke={
                    efficiencyScore >= 75 ? "#22c55e" : 
                    efficiencyScore >= 50 ? "#3b82f6" : 
                    efficiencyScore >= 30 ? "#eab308" : 
                    "#ef4444"
                  }
                  strokeWidth="3"
                  strokeDasharray={`${efficiencyScore}, 100`}
                />
              </svg>
            </div>
            
            <div className="mt-4 space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-blue-600" />
                  Avg. Time per Question
                </span>
                <span className="font-medium">{Math.round(averageTime)}s</span>
              </div>
              
              <div className="flex justify-between items-center text-sm">
                <span className="flex items-center gap-1.5">
                  <Zap className="h-3.5 w-3.5 text-amber-600" />
                  Quickest Response
                </span>
                <span className="font-medium">{fastestQuestion}s</span>
              </div>
              
              <div className="flex justify-between items-center text-sm">
                <span className="flex items-center gap-1.5">
                  <Brain className="h-3.5 w-3.5 text-indigo-600" />
                  Longest Consideration
                </span>
                <span className="font-medium">{slowestQuestion}s</span>
              </div>
              
              <div className="flex justify-between items-center text-sm">
                <span className="flex items-center gap-1.5">
                  <LineChart className="h-3.5 w-3.5 text-red-600" />
                  Focus Fluctuation
                </span>
                <span className="font-medium">
                  {burnoutZones.length === 0 ? 'Low' : burnoutZones.length === 1 ? 'Moderate' : 'High'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="md:w-2/3">
            <h3 className="text-lg font-semibold mb-4 flex items-center text-indigo-800">
              <BrainCircuit className="h-5 w-5 mr-2 text-indigo-600" />
              Cognitive Performance Analysis
            </h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-2 text-gray-700">Focus Consistency</h4>
                <div className="mb-1 flex justify-between text-xs">
                  <span>Erratic</span>
                  <span>Steady</span>
                </div>
                <Progress value={100 - Math.min(jumpCount * 5, 70)} className="h-2" />
              </div>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {burnoutZones.length > 0 && (
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                    <Gauge className="h-3.5 w-3.5 mr-1.5" />
                    Fatigue Detected
                  </Badge>
                )}
                
                {Object.values(questionTimings).filter(time => time < averageTime * 0.3).length > 5 && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    <Zap className="h-3.5 w-3.5 mr-1.5" />
                    Speed Response Pattern
                  </Badge>
                )}
                
                {burnoutZones.length === 0 && jumpCount < 5 && (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <Brain className="h-3.5 w-3.5 mr-1.5" />
                    Consistent Focus
                  </Badge>
                )}
              </div>
              
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center">
                  <ChevronRight className="h-4 w-4 text-indigo-500" />
                  Key Insights
                </h4>
                <ul className="space-y-2">
                  {insights.map((insight, idx) => (
                    <li key={idx} className="text-sm text-gray-700 flex items-start">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-indigo-500 mt-1.5 mr-2 flex-shrink-0"></span>
                      {insight}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="text-sm text-gray-600 pt-2 mt-2 border-t border-gray-100">
                <p><strong>What This Means:</strong> {
                  efficiencyScore >= 75 
                    ? "You maintained excellent focus throughout the test with consistent response patterns."
                    : efficiencyScore >= 50
                    ? "You showed good overall focus with some fluctuations in attention."
                    : efficiencyScore >= 30
                    ? "Your focus varied significantly throughout the test, with noticeable fatigue points."
                    : "Your test-taking pattern shows signs of inconsistent focus and potential burnout periods."
                }</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CognitiveFatigueReport;
